/**
 * @description Hook that sets document.title based on current route path
 *
 * Key features:
 * - Static route → title map for all merchant and SO pages
 * - Dynamic pattern matching for parameterized routes (invoices/:id, etc.)
 * - Fallback: capitalizes last URL segment for unmapped routes
 * - Reads brand name from BrandingContext (tenant-aware)
 * - Format: "PageTitle | BrandName"
 *
 * @dependencies
 * - react-router-dom: useLocation for current path
 * - BrandingContext: Platform/tenant name for brand suffix
 *
 * @notes
 * - Called by BaseSidebarLayout (covers all merchant + SO routes)
 * - Auth pages use usePageTitle hook directly instead
 * - To add a new route title, add an entry to ROUTE_TITLES or DYNAMIC_ROUTE_PATTERNS
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useBranding } from '../contexts/BrandingContext';

// Static route → page title mapping (merchant + SO)
const ROUTE_TITLES = {
  '/merchant/dashboard': 'Dashboard',
  '/merchant/transactions': 'Transactions',
  '/merchant/reporting': 'Reporting',
  '/merchant/customers': 'Customers',
  '/merchant/products': 'Products',
  '/merchant/company': 'Company',
  '/merchant/website': 'Website',
  '/merchant/balances': 'Balances',
  '/merchant/invoices/new': 'New Invoice',
  '/merchant/credit-memos': 'Credit Memos',
  '/merchant/profile': 'Profile',
  '/merchant/outreach': 'Outreach',
  '/merchant/outreach/activity': 'Email Activity',
  '/merchant/outreach/templates': 'Email Templates',
  '/merchant/settings': 'Settings',
  '/so/dashboard': 'Dashboard',
  '/so/assets-crew': 'Assets & Crew',
  '/so/employees': 'Employees',
  '/so/employees/pending-matches': 'Pending matches',
  '/so/company': 'Company',
  '/so/transactions': 'Transactions',
  '/so/merchants': 'Merchants',
  '/so/reporting': 'Reporting',
  '/so/profile': 'Profile',
  '/so/settings': 'Settings',
  '/so/settings/connectors': 'Connectors',
  '/cp/dashboard': 'Dashboard',
  '/cp/sales-orgs': 'Sales Orgs',
  '/cp/merchants': 'Merchants',
  '/cp/my-company': 'My Company',
  '/cp/integrations': 'Integrations',
  '/cp/settings': 'Settings',
};

// Dynamic route patterns — more specific patterns first
const DYNAMIC_ROUTE_PATTERNS = [
  { pattern: /^\/merchant\/invoices\/([^/]+)\/payment$/, title: () => 'Record Payment' },
  { pattern: /^\/merchant\/invoices\/([^/]+)\/edit$/, title: (m) => `Edit Invoice ${m[1]}` },
  { pattern: /^\/merchant\/invoices\/([^/]+)$/, title: (m) => `Invoice ${m[1]}` },
  { pattern: /^\/merchant\/transactions\/([^/]+)$/, title: () => 'Transaction Details' },
  { pattern: /^\/merchant\/customers\/([^/]+)$/, title: () => 'Customer Details' },
  { pattern: /^\/so\/transactions\/([^/]+)$/, title: () => 'Transaction Details' },
  { pattern: /^\/so\/merchants\/([^/]+)$/, title: () => 'Merchant Details' },
  { pattern: /^\/merchant\/profile\//, title: () => 'Profile' },
  { pattern: /^\/so\/profile\//, title: () => 'Profile' },
];

function resolvePageTitle(pathname) {
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];
  for (const { pattern, title } of DYNAMIC_ROUTE_PATTERNS) {
    const match = pathname.match(pattern);
    if (match) return title(match);
  }
  // Fallback: capitalize last path segment
  const last = pathname.split('/').filter(Boolean).pop() || 'Page';
  return last.charAt(0).toUpperCase() + last.slice(1).replace(/-/g, ' ');
}

export default function useRouteTitle() {
  const { branding } = useBranding();
  const brandName = branding.identity.documentTitle || branding.identity.platformName;
  const location = useLocation();

  useEffect(() => {
    const pageTitle = resolvePageTitle(location.pathname);
    document.title = `${pageTitle} | ${brandName}`;
  }, [location.pathname, brandName]);
}
